import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * TrackingCodeInjector Component
 * Fetches tracking codes from backend and injects them into the page
 * Runs on every page load
 */
const TrackingCodeInjector = () => {
  const location = useLocation();

  useEffect(() => {
    const injectTrackingCodes = async () => {
      try {
        // Fetch tracking codes from public endpoint
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const response = await fetch(`${backendUrl}/api/public/tracking-codes`);
        
        if (!response.ok) {
          console.warn('Failed to fetch tracking codes');
          return;
        }

        const codes = await response.json();

        // Inject global header code (only once)
        if (codes.global_header_code && !document.getElementById('global-header-tracking')) {
          const headerScript = document.createElement('div');
          headerScript.id = 'global-header-tracking';
          headerScript.innerHTML = codes.global_header_code;
          
          // Move script tags to head
          const scripts = headerScript.querySelectorAll('script');
          scripts.forEach(script => {
            const newScript = document.createElement('script');
            if (script.src) {
              newScript.src = script.src;
              if (script.async) newScript.async = true;
              if (script.defer) newScript.defer = true;
            } else {
              newScript.textContent = script.textContent;
            }
            document.head.appendChild(newScript);
          });

          // Move non-script tags to head
          const otherTags = headerScript.querySelectorAll(':not(script)');
          otherTags.forEach(tag => {
            document.head.appendChild(tag.cloneNode(true));
          });
        }

        // Inject global footer code (only once)
        if (codes.global_footer_code && !document.getElementById('global-footer-tracking')) {
          const footerScript = document.createElement('div');
          footerScript.id = 'global-footer-tracking';
          footerScript.innerHTML = codes.global_footer_code;
          
          const scripts = footerScript.querySelectorAll('script');
          scripts.forEach(script => {
            const newScript = document.createElement('script');
            if (script.src) {
              newScript.src = script.src;
              if (script.async) newScript.async = true;
              if (script.defer) newScript.defer = true;
            } else {
              newScript.textContent = script.textContent;
            }
            document.body.appendChild(newScript);
          });
        }

        // Inject checkout success code (only on /checkout/success page)
        if (location.pathname === '/checkout/success' && codes.checkout_success_code) {
          // Remove old checkout tracking if exists
          const oldCheckoutTracking = document.getElementById('checkout-success-tracking');
          if (oldCheckoutTracking) {
            oldCheckoutTracking.remove();
          }

          const checkoutScript = document.createElement('div');
          checkoutScript.id = 'checkout-success-tracking';
          checkoutScript.innerHTML = codes.checkout_success_code;
          
          const scripts = checkoutScript.querySelectorAll('script');
          scripts.forEach(script => {
            const newScript = document.createElement('script');
            if (script.src) {
              newScript.src = script.src;
              if (script.async) newScript.async = true;
            } else {
              newScript.textContent = script.textContent;
            }
            document.body.appendChild(newScript);
          });

          console.log('✅ Checkout success tracking code injected');
        }

        // Inject landing page code (only on / homepage)
        if (location.pathname === '/' && codes.landing_page_code) {
          // Remove old landing tracking if exists
          const oldLandingTracking = document.getElementById('landing-page-tracking');
          if (oldLandingTracking) {
            oldLandingTracking.remove();
          }

          const landingScript = document.createElement('div');
          landingScript.id = 'landing-page-tracking';
          landingScript.innerHTML = codes.landing_page_code;
          
          const scripts = landingScript.querySelectorAll('script');
          scripts.forEach(script => {
            const newScript = document.createElement('script');
            if (script.src) {
              newScript.src = script.src;
              if (script.async) newScript.async = true;
            } else {
              newScript.textContent = script.textContent;
            }
            document.body.appendChild(newScript);
          });

          console.log('✅ Landing page tracking code injected');
        }

      } catch (error) {
        console.error('Error injecting tracking codes:', error);
      }
    };

    injectTrackingCodes();
  }, [location.pathname]); // Re-run when route changes

  return null; // This component doesn't render anything
};

export default TrackingCodeInjector;
